import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../utils/constants';
import apiService from '../services/api';
import CourseCard from '../components/courses/CourseCard';
import StorybookCard from '../components/storybooks/StorybookCard';

const MyContentPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [purchasedContent, setPurchasedContent] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [filterType, setFilterType] = useState('all'); // all, course, storybook
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!loading && !user) {
      navigate(ROUTES.LOGIN);
    } else if (user) {
      loadContent();
      loadUnreadCount();
    }
  }, [user, loading, navigate]);

  const loadUnreadCount = async () => {
    try {
      const readNotificationIds = JSON.parse(localStorage.getItem('readNotifications') || '[]');
      const generatedNotifications = [];
      
      const purchases = await apiService.getUserPurchases();
      purchases.forEach((purchase) => {
        const notifId = `purchase-${purchase.id}`;
        generatedNotifications.push({
          id: notifId,
          read: readNotificationIds.includes(notifId)
        });
      });

      const manualPayments = await apiService.getUserManualPayments();
      manualPayments.forEach((payment) => {
        const notifId = `payment-${payment.id}`;
        generatedNotifications.push({
          id: notifId,
          read: readNotificationIds.includes(notifId)
        });
      });

      const unread = generatedNotifications.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error loading notification count:', err);
    }
  };

  const loadContent = async () => {
    try {
      setLoadingData(true);
      const purchases = await apiService.getUserPurchases();
      setPurchasedContent(purchases);
    } catch (err) {
      console.error('Error loading content:', err);
    } finally {
      setLoadingData(false);
    }
  };

  const renderPurchaseTypeBadge = (item) => {
    if (!item.purchase_type || item.purchase_type === 'individual') {
      return (
        <div className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
          <i className="fas fa-shopping-cart"></i>
          <span>مشترى منفرد</span>
          <span className="text-green-600">(دائم)</span>
        </div>
      );
    }

    switch (item.purchase_type) {
      case 'subscription':
        return (
          <div className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
            <i className="fas fa-calendar-check"></i>
            <span>الاشتراك الشهري</span>
            <span className="text-orange-600">(مؤقت)</span>
          </div>
        );
      case 'bundle':
        return (
          <div className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
            <i className="fas fa-gift"></i>
            <span>ضمن الحزمة #{item.bundle_id || '?'}</span>
            <span className="text-green-600">(دائم)</span>
          </div>
        );
      default:
        return null;
    }
  };

  // Filter content
  const filteredContent = purchasedContent.filter(item => {
    // Filter by type
    if (filterType !== 'all' && item.content_type !== filterType) return false;
    
    // Filter by search query
    if (searchQuery) {
      const title = item.title || '';
      if (!title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
    }
    
    return true;
  });

  if (loading || loadingData) {
    return (
      <div className="relative z-10 min-h-screen flex items-center justify-center bg-bg">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text3">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="relative z-10 py-12 md:py-20 bg-gradient-to-b from-bg to-white min-h-screen">
      <div className="container mx-auto px-3 md:px-4">
        <div className="max-w-[1400px] mx-auto">
          {/* Header */}
          <div className="mb-8 md:mb-12">
            <Link
              to={ROUTES.DASHBOARD}
              className="inline-flex items-center gap-2 text-primary hover:underline font-bold mb-3 md:mb-4 text-sm md:text-base"
            >
              <i className="fas fa-arrow-right"></i>
              العودة للوحة التحكم
            </Link>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-text mb-2">محتواي التعليمي</h1>
            <p className="text-sm md:text-lg text-text3">جميع الدورات والقصص المصورة اللي شريتها</p>
          </div>

          {/* Stats Cards - All in one row on mobile */}
          <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6 md:mb-8">
            <div className="bg-white rounded-lg md:rounded-xl lg:rounded-2xl p-2 md:p-4 lg:p-6 shadow-custom">
              <div className="flex flex-col md:flex-row items-center md:gap-3">
                <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg md:rounded-xl flex items-center justify-center text-white text-sm md:text-base mb-1 md:mb-0">
                  <i className="fas fa-book"></i>
                </div>
                <div className="text-center md:text-right">
                  <div className="text-lg md:text-2xl font-black text-blue-600">{purchasedContent.length}</div>
                  <div className="text-[10px] md:text-xs lg:text-sm text-text3 leading-tight">إجمالي المحتوى</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg md:rounded-xl lg:rounded-2xl p-2 md:p-4 lg:p-6 shadow-custom">
              <div className="flex flex-col md:flex-row items-center md:gap-3">
                <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg md:rounded-xl flex items-center justify-center text-white text-sm md:text-base mb-1 md:mb-0">
                  <i className="fas fa-graduation-cap"></i>
                </div>
                <div className="text-center md:text-right">
                  <div className="text-lg md:text-2xl font-black text-blue-600">
                    {purchasedContent.filter(c => c.content_type === 'course').length}
                  </div>
                  <div className="text-[10px] md:text-xs lg:text-sm text-text3 leading-tight">دورات</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg md:rounded-xl lg:rounded-2xl p-2 md:p-4 lg:p-6 shadow-custom">
              <div className="flex flex-col md:flex-row items-center md:gap-3">
                <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg md:rounded-xl flex items-center justify-center text-white text-sm md:text-base mb-1 md:mb-0">
                  <i className="fas fa-book-open"></i>
                </div>
                <div className="text-center md:text-right">
                  <div className="text-lg md:text-2xl font-black text-blue-600">
                    {purchasedContent.filter(c => c.content_type === 'storybook').length}
                  </div>
                  <div className="text-[10px] md:text-xs lg:text-sm text-text3 leading-tight">قصص</div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-custom p-3 md:p-4 mb-6 md:mb-8">
            <div className="flex flex-col md:grid md:grid-cols-2 gap-3 md:gap-4">
              {/* Search */}
              <div className="relative">
                <i className="fas fa-search absolute right-3 top-1/2 -translate-y-1/2 text-text3 text-sm md:text-base"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ابحث عن محتوى..."
                  className="w-full pr-10 pl-3 md:pl-4 py-2.5 md:py-3 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-primary focus:outline-none font-bold text-sm md:text-base"
                />
              </div>

              {/* Filter Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setFilterType('all')}
                  className={`flex-1 px-3 md:px-4 py-2.5 md:py-3 rounded-lg md:rounded-xl font-bold transition-all duration-300 text-xs md:text-base ${
                    filterType === 'all'
                      ? 'bg-gradient-to-br from-primary to-primary-dark text-white shadow-lg'
                      : 'bg-gray-100 text-text3 hover:bg-gray-200'
                  }`}
                >
                  <i className="fas fa-th ml-1 md:ml-2 text-xs md:text-base"></i>
                  <span className="hidden sm:inline">الكل ({purchasedContent.length})</span>
                  <span className="sm:hidden">({purchasedContent.length})</span>
                </button>
                <button
                  onClick={() => setFilterType('course')}
                  className={`flex-1 px-3 md:px-4 py-2.5 md:py-3 rounded-lg md:rounded-xl font-bold transition-all duration-300 text-xs md:text-base ${
                    filterType === 'course'
                      ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-text3 hover:bg-gray-200'
                  }`}
                >
                  <i className="fas fa-graduation-cap ml-1 md:ml-2 text-xs md:text-base"></i>
                  <span className="hidden sm:inline">دورات ({purchasedContent.filter(c => c.content_type === 'course').length})</span>
                  <span className="sm:hidden">({purchasedContent.filter(c => c.content_type === 'course').length})</span>
                </button>
                <button
                  onClick={() => setFilterType('storybook')}
                  className={`flex-1 px-3 md:px-4 py-2.5 md:py-3 rounded-lg md:rounded-xl font-bold transition-all duration-300 text-xs md:text-base ${
                    filterType === 'storybook'
                      ? 'bg-gradient-to-br from-purple-500 to-pink-600 text-white shadow-lg'
                      : 'bg-gray-100 text-text3 hover:bg-gray-200'
                  }`}
                >
                  <i className="fas fa-book-open ml-1 md:ml-2 text-xs md:text-base"></i>
                  <span className="hidden sm:inline">قصص ({purchasedContent.filter(c => c.content_type === 'storybook').length})</span>
                  <span className="sm:hidden">({purchasedContent.filter(c => c.content_type === 'storybook').length})</span>
                </button>
              </div>
            </div>
          </div>

          {/* Results Count */}
          {searchQuery && (
            <div className="mb-3 md:mb-4 text-sm md:text-base text-text2">
              عرض <span className="font-bold text-primary">{filteredContent.length}</span> من أصل{' '}
              <span className="font-bold">{purchasedContent.length}</span> محتوى
            </div>
          )}

          {/* Content Cards - 2 per row on mobile */}
          {filteredContent.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8">
              {filteredContent.map(item => (
                <div key={`${item.content_type}-${item.id}`}>
                  {/* Purchase Type Badge - Above the card */}
                  <div className="mb-2">
                    {renderPurchaseTypeBadge(item)}
                  </div>
                  
                  {item.content_type === 'storybook' ? (
                    <StorybookCard 
                      storybook={item} 
                      isPurchased={true}
                    />
                  ) : (
                    <CourseCard 
                      course={item} 
                      isPurchased={true}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12 text-center shadow-custom">
              <div className="w-16 h-16 md:w-24 md:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="fas fa-search text-2xl md:text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-lg md:text-2xl font-black text-text mb-2">لا توجد نتائج</h3>
              <p className="text-sm md:text-base text-text3 mb-4 md:mb-6">حاول البحث بكلمات مختلفة أو غير الفلتر</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterType('all');
                }}
                className="inline-flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-gradient-to-br from-primary to-primary-dark text-white rounded-lg md:rounded-xl font-bold hover:shadow-lg transition-all duration-300 text-sm md:text-base"
              >
                <i className="fas fa-redo"></i>
                <span>إعادة تعيين الفلاتر</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Navigation - Mobile Only */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50 safe-area-bottom">
        <div className="grid grid-cols-3 gap-1 px-2 py-1">
          <Link
            to="/notifications"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors relative"
          >
            <i className="fas fa-bell text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">الإشعارات</span>
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Link>
          
          <Link
            to={ROUTES.DASHBOARD}
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors relative"
          >
            <i className="fas fa-home text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">لوحة التحكم</span>
          </Link>
          
          <Link
            to="/profile"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="fas fa-user text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">الملف الشخصي</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MyContentPage;
